// design/Teams.dc.html. FR-T1: "Admins and Members ... create teams" — teams are member-level
// territory, not admin-only (unlike the admin/ directory's pages). Any org member can see every
// team, create one, and manage a team they're that team's own admin of; an org admin/owner can
// additionally manage *any* team. teams.rs already enforces exactly this
// (`authorize_team_write`); this page is the UI that was missing for anyone who isn't an org
// admin/owner — the only Teams surface that existed before lived inside the admin-only console.
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail } from "../api/client";
import type {
  ListResponse,
  MemberOut,
  TeamActivityItem,
  TeamMemberDetailOut,
  TeamOut,
  TeamSpaceOut,
  TeamVisibility,
} from "../api/types";
import { useSession } from "../session";
import { Avatar, Badge, Card, Icon, IconButton, PersonPicker, Select } from "@grackle/ui";
import { AppShell } from "../components/AppShell";
import { Button, Field, Notice, PageLoader } from "../components/ui";
import "./library.css";
import "../admin/admin.css";

export function TeamRoster({
  orgId,
  team,
  allMembers,
  myUserId,
  isOrgAdmin,
  onChanged,
  onDeleteTeam,
}: {
  orgId: string;
  team: TeamOut;
  allMembers: MemberOut[];
  myUserId: string;
  isOrgAdmin: boolean;
  onChanged: () => void;
  onDeleteTeam: () => void;
}) {
  const [roster, setRoster] = useState<TeamMemberDetailOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [joining, setJoining] = useState(false);

  const load = useCallback(() => {
    api<ListResponse<TeamMemberDetailOut>>("GET", `/orgs/${orgId}/teams/${team.id}/members`)
      .then((res) => setRoster(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load this team's members."));
  }, [orgId, team.id]);

  useEffect(() => {
    load();
  }, [load]);

  const canManage = isOrgAdmin || (roster ?? []).some((m) => m.user_id === myUserId && m.role === "admin");

  async function onAdd(userId: string) {
    setAdding(true);
    setError(null);
    try {
      await api("POST", `/orgs/${orgId}/teams/${team.id}/members`, { user_id: userId });
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't add that member.");
    } finally {
      setAdding(false);
    }
  }

  async function onRemove(userId: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${orgId}/teams/${team.id}/members/${userId}`);
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't remove that member.");
    }
  }

  async function onRoleChange(userId: string, role: "admin" | "member") {
    setError(null);
    try {
      await api("PATCH", `/orgs/${orgId}/teams/${team.id}/members/${userId}`, { role });
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't change that member's role.");
    }
  }

  const rosterIds = new Set((roster ?? []).map((r) => r.user_id));
  const addable = allMembers.filter((m) => !rosterIds.has(m.user.id));
  const iAmMember = roster !== null && rosterIds.has(myUserId);

  async function onJoin() {
    setJoining(true);
    setError(null);
    try {
      await api("POST", `/orgs/${orgId}/teams/${team.id}/join`);
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't join this team.");
    } finally {
      setJoining(false);
    }
  }

  return (
    <div className="admin-team-body">
      {team.description ? <div className="admin-row-sub" style={{ marginBottom: 6 }}>{team.description}</div> : null}
      {team.visibility === "open" && roster !== null && !iAmMember ? (
        <Notice>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span>This is an open team — anyone in the org can join.</span>
            <Button variant="secondary" onClick={onJoin} loading={joining} style={{ height: 28, fontSize: 11.5 }}>
              Join
            </Button>
          </div>
        </Notice>
      ) : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
      {roster === null ? (
        <div className="admin-row-sub">Loading…</div>
      ) : roster.length === 0 ? (
        <div className="admin-row-sub">No members yet.</div>
      ) : (
        <Card size="flush" className="admin-table-card">
<table className="admin-table">
          <tbody>
            {roster.map((m) => (
              <tr key={m.user_id}>
                <td>
                  <div className="admin-row-name">
                    {m.name || m.email}
                    {m.user_id === myUserId ? " (you)" : ""}
                  </div>
                  <div className="admin-row-sub">{m.email}</div>
                </td>
                <td>
                  {canManage ? (
                    <Select
                      size="sm"
                      aria-label={`Role for ${m.name || m.email}`}
                      value={m.role}
                      onChange={(v) => onRoleChange(m.user_id, v as "admin" | "member")}
                      options={[
                        { value: "admin", label: "Team admin" },
                        { value: "member", label: "Member" },
                      ]}
                    />
                  ) : (
                    <Badge size="sm">{m.role === "admin" ? "Team admin" : "Member"}</Badge>
                  )}
                </td>
                <td>
                  {canManage ? (
                    <div className="row-actions">
                      <IconButton icon="trash" label={`Remove ${m.name || m.email} from ${team.name}`} size="sm" variant="muted" onClick={() => onRemove(m.user_id)} />
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
</Card>
      )}

      {canManage && addable.length > 0 ? (
        // A type-to-search picker rather than a Select of every org member: it scales with the org,
        // and picking adds at once (the new row appears in the table above, with Remove beside it).
        <PersonPicker
          className="team-add-member"
          inputLabel="Add a team member"
          placeholder="Add a member by name or email…"
          options={addable.map((m) => ({ id: m.user.id, name: m.user.name || m.user.email, email: m.user.email }))}
          onPick={(person) => void onAdd(person.id)}
          disabled={adding}
        />
      ) : null}

      <TeamSpaces orgId={orgId} teamId={team.id} canManage={canManage} />
      <TeamActivity orgId={orgId} teamId={team.id} />

      {canManage ? (
        <div style={{ paddingTop: 4 }}>
          <Button variant="secondary" size="sm" onClick={onDeleteTeam}>
            Delete team
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function TeamSpaces({ orgId, teamId, canManage }: { orgId: string; teamId: string; canManage: boolean }) {
  const [spaces, setSpaces] = useState<TeamSpaceOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api<ListResponse<TeamSpaceOut>>("GET", `/orgs/${orgId}/teams/${teamId}/spaces`)
      .then((res) => setSpaces(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load spaces."));
  }, [orgId, teamId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      await api("POST", `/orgs/${orgId}/teams/${teamId}/spaces`, { name });
      setNewName("");
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't create that space.");
    } finally {
      setCreating(false);
    }
  }

  async function onDelete(spaceId: string) {
    setError(null);
    try {
      await api("DELETE", `/orgs/${orgId}/teams/${teamId}/spaces/${spaceId}`);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't delete that space.");
    }
  }

  return (
    <div style={{ paddingTop: 4 }}>
      <div className="admin-row-sub" style={{ marginBottom: 6 }}>Spaces</div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!spaces ? null : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: canManage ? 8 : 0 }}>
          {spaces.map((s) => (
            <Badge key={s.id} size="sm" tone="neutral">
              {s.name}
              {canManage && !s.is_default ? (
                <IconButton icon="x" label={`Delete space ${s.name}`} size="sm" variant="muted" onClick={() => onDelete(s.id)} />
              ) : null}
            </Badge>
          ))}
        </div>
      )}
      {canManage ? (
        <div className="admin-inline-form">
          <Field label="New space" placeholder="Client work" value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Button variant="secondary" onClick={onCreate} loading={creating} disabled={!newName.trim()} style={{ marginTop: 22, height: 32 }}>
            Add
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function TeamActivity({ orgId, teamId }: { orgId: string; teamId: string }) {
  const [items, setItems] = useState<TeamActivityItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<ListResponse<TeamActivityItem>>("GET", `/orgs/${orgId}/teams/${teamId}/activity`)
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load activity."));
  }, [orgId, teamId]);

  return (
    <div style={{ paddingTop: 4 }}>
      <div className="admin-row-sub" style={{ marginBottom: 6 }}>Recently shared with this team</div>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {!items ? (
        <div className="admin-row-sub">Loading…</div>
      ) : items.length === 0 ? (
        <div className="admin-row-sub">Nothing has been shared with this team yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it) => (
            <Link
              key={it.grackle_id}
              to={`/g/${it.grackle_id}`}
              className="list-row"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <div className="list-row__body">
                <div className="list-row__title">{it.title || "Untitled"}</div>
                <div className="list-row__sub">
                  {it.owner_name} · {new Date(it.shared_at).toLocaleDateString()}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamsPage() {
  const { slug } = useParams();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [teams, setTeams] = useState<TeamOut[]>([]);
  const [members, setMembers] = useState<MemberOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamVisibility, setNewTeamVisibility] = useState<TeamVisibility>("closed");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<TeamOut | null>(null);

  const load = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    setError(null);
    try {
      const [teamsRes, membersRes] = await Promise.all([
        api<ListResponse<TeamOut>>("GET", `/orgs/${org.id}/teams`),
        api<ListResponse<MemberOut>>("GET", `/orgs/${org.id}/members`),
      ]);
      setTeams(teamsRes.items);
      setMembers(membersRes.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load teams.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (sessionLoading) return <PageLoader label="Loading teams…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  const isOrgAdmin = org.role === "owner" || org.role === "admin";

  async function onCreate() {
    if (!org) return;
    const name = newTeamName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      await api("POST", `/orgs/${org.id}/teams`, {
        name,
        visibility: newTeamVisibility,
      });
      setNewTeamName("");
      setNewTeamVisibility("closed");
      await load();
    } catch (err) {
      setCreateError(err instanceof ApiFail ? err.message : "Couldn't create that team.");
    } finally {
      setCreating(false);
    }
  }

  async function onDeleteConfirm() {
    if (!deleteConfirm || !org) return;
    const { id } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await api("DELETE", `/orgs/${org.id}/teams/${id}`);
      if (expanded === id) setExpanded(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't delete that team.");
    }
  }

  function onDelete(team: TeamOut) {
    setDeleteConfirm(team);
  }

  return (
    <AppShell org={org} user={user} section="teams">
        <div className="admin-content">
          <header className="page-head">
            <div>
              <h1>Teams</h1>
              <p>Flat groups inside {org.name} — used for team-scoped sharing.</p>
            </div>
          </header>

          <Card size="sm" title="New team">
            <div className="admin-inline-form">
              <Field
                label="Name"
                placeholder="Design"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
              />
              <Select
                label="Visibility"
                value={newTeamVisibility}
                onChange={(v) => setNewTeamVisibility(v as TeamVisibility)}
                options={[
                  { value: "closed", label: "Invite-only" },
                  { value: "open", label: "Open — anyone can join" },
                ]}
              />
              <Button onClick={onCreate} loading={creating} disabled={!newTeamName.trim()}>
                Create team
              </Button>
            </div>
            {createError ? <Notice tone="error">{createError}</Notice> : null}
          </Card>

          {error ? <Notice tone="error">{error}</Notice> : null}

          {loading ? (
            <div className="admin-row-sub">Loading…</div>
          ) : teams.length === 0 ? (
            <div className="admin-row-sub">No teams yet — create one above.</div>
          ) : (
            <div className="stack">
              {teams.map((team) => {
                const open = expanded === team.id;
                return (
                  <Card size="sm" className="admin-team-card" key={team.id}>
                    <button type="button" className="admin-team-head" aria-expanded={open} onClick={() => setExpanded(open ? null : team.id)}>
                      <Icon name="caret-up-down" size={18} />
                      {/* DS: no emoji in UI, so the team's initials stand in for avatar_emoji. PR7 removed the field
                          from the API; a later cleanup migration drops the column. */}
                      <Avatar name={team.name} size={24} square aria-hidden />
                      <span className="admin-team-name">{team.name}</span>
                      {team.visibility === "open" ? <Badge size="sm">Open</Badge> : null}
                      <span className="admin-team-count">
                        {team.member_count} member{team.member_count === 1 ? "" : "s"}
                      </span>
                    </button>
                    {open ? (
                      <TeamRoster
                        orgId={org.id}
                        team={team}
                        allMembers={members}
                        myUserId={user.id}
                        isOrgAdmin={isOrgAdmin}
                        onChanged={load}
                        onDeleteTeam={() => onDelete(team)}
                      />
                    ) : null}
                  </Card>
                );
              })}
            </div>
          )}
        </div>

      {deleteConfirm ? (
        <ConfirmDialog
          open
          title="Delete team"
          message={`Delete ${deleteConfirm.name}? This can't be undone.`}
          confirmLabel="Delete team"
          danger
          onClose={() => setDeleteConfirm(null)}
          onConfirm={onDeleteConfirm}
        />
      ) : null}
    </AppShell>
  );
}
